import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Chip,
  Stack,
  Button,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  History as HistoryIcon,
  FactCheck as FactCheckIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AssignmentInd as AssignmentIndIcon,
  Draw as DrawIcon,
  ExpandMore as ExpandMoreIcon,
  PersonOutline as PersonOutlineIcon,
  FilterAltOutlined as FilterAltIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { auditService, usersService } from '../services/api';
import { ROLE_LABELS } from './Login';

dayjs.locale('it');

const BRAND = '#fd0000';

// --- Configurazione visiva per ogni AZIONE ----------------------------------
const AZIONI = {
  crea: { label: 'Creato', color: 'success', icon: <AddIcon fontSize="small" /> },
  modifica: { label: 'Modificato', color: 'info', icon: <EditIcon fontSize="small" /> },
  elimina: { label: 'Eliminato', color: 'error', icon: <DeleteIcon fontSize="small" /> },
  assegna: { label: 'Assegnato', color: 'warning', icon: <AssignmentIndIcon fontSize="small" /> },
  firma: { label: 'Firmato', color: 'secondary', icon: <DrawIcon fontSize="small" /> },
};

// --- Configurazione visiva per ogni ENTITA ----------------------------------
const ENTITA = {
  cliente: { label: 'Cliente' },
  cantiere: { label: 'Cantiere' },
  intervento: { label: 'Intervento' },
  rapportino: { label: 'Rapportino' },
  utente: { label: 'Utente' },
  veicolo: { label: 'Veicolo' },
};

const AZIONE_OPTIONS = Object.keys(AZIONI);
const ENTITA_OPTIONS = Object.keys(ENTITA);

// Etichette "umane" per i campi noti dei dettagli
const DETTAGLI_LABELS = {
  tecnici: 'Tecnici',
  capocantiere: 'Capocantiere',
  ore_lavorate: 'Ore lavorate',
  cliente: 'Cliente',
  cantiere: 'Cantiere',
  data: 'Data',
  tipologia: 'Tipologia',
  stato: 'Stato',
  firmatario: 'Firmatario',
};

const formatValoreDettaglio = (valore) => {
  if (valore === null || valore === undefined) return '—';
  if (Array.isArray(valore)) return valore.join(', ');
  if (typeof valore === 'object') return JSON.stringify(valore);
  return String(valore);
};

const formatLabelDettaglio = (chiave) =>
  DETTAGLI_LABELS[chiave] ||
  chiave.charAt(0).toUpperCase() + chiave.slice(1).replace(/_/g, ' ');

const getAzioneConfig = (azione) =>
  AZIONI[azione] || { label: azione || 'Azione', color: 'default', icon: <HistoryIcon fontSize="small" /> };

const getEntitaConfig = (entita) =>
  ENTITA[entita] || { label: entita || 'Voce' };

// =============================================================================
// Singola voce dell'audit log (card della timeline)
// =============================================================================
const VoceAudit = ({ voce }) => {
  const azioneCfg = getAzioneConfig(voce.azione);
  const entitaCfg = getEntitaConfig(voce.entita);

  const data = voce.timestamp ? dayjs(voce.timestamp) : null;
  const orarioCompleto = data && data.isValid() ? data.format('DD/MM/YYYY HH:mm') : '—';

  // Estrae i campi "chiave" dei dettagli (se presenti)
  const dettagliEntries =
    voce.dettagli && typeof voce.dettagli === 'object'
      ? Object.entries(voce.dettagli).filter(
          ([, v]) => v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)
        )
      : [];

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `4px solid`,
        borderLeftColor: `${azioneCfg.color}.main`,
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <CardContent sx={{ p: { xs: 1.75, sm: 2.25 }, '&:last-child': { pb: { xs: 1.75, sm: 2.25 } } }}>
        {/* Riga superiore: chip azione + entità + orario */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}
        >
          <Chip
            size="small"
            icon={azioneCfg.icon}
            label={azioneCfg.label}
            color={azioneCfg.color}
            sx={{ fontWeight: 700 }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={entitaCfg.label}
            sx={{ fontWeight: 600 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {orarioCompleto}
          </Typography>
        </Stack>

        {/* Descrizione principale */}
        <Typography
          variant="body1"
          sx={{ color: 'text.primary', fontWeight: 500, lineHeight: 1.45, mb: 0.75 }}
        >
          {voce.descrizione || 'Attività registrata'}
        </Typography>

        {/* Autore + ruolo */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Avatar
            sx={{
              width: 24,
              height: 24,
              bgcolor: 'rgba(0,0,0,0.06)',
              color: 'text.secondary',
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Typography variant="body2" color="text.secondary">
            da{' '}
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {voce.user_nome || 'Sconosciuto'}
            </Box>
            {voce.user_role ? ` · ${ROLE_LABELS[voce.user_role] || voce.user_role}` : ''}
          </Typography>
        </Stack>

        {/* Dettagli (discreti, in un accordion) */}
        {dettagliEntries.length > 0 && (
          <Accordion
            disableGutters
            elevation={0}
            square
            sx={{
              mt: 1,
              bgcolor: 'transparent',
              '&:before': { display: 'none' },
              border: 'none',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon fontSize="small" />}
              sx={{
                minHeight: 0,
                px: 0,
                py: 0.25,
                '& .MuiAccordionSummary-content': { my: 0 },
              }}
            >
              <Typography variant="caption" sx={{ color: BRAND, fontWeight: 600 }}>
                Dettagli
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0.5, pb: 0 }}>
              <Stack spacing={0.5} divider={<Divider flexItem />}>
                {dettagliEntries.map(([chiave, valore]) => (
                  <Box
                    key={chiave}
                    sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'baseline' }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 100, fontWeight: 600 }}
                    >
                      {formatLabelDettaglio(chiave)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
                      {formatValoreDettaglio(valore)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
};

// =============================================================================
// Pagina Registro Attività
// =============================================================================
const RegistroAttivita = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Dati
  const [voci, setVoci] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [mostraFiltroAutore, setMostraFiltroAutore] = useState(false);

  // Stato UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtri
  const [qInput, setQInput] = useState(''); // valore digitato (immediato)
  const [q, setQ] = useState(''); // valore "debounced" usato per la query
  const [azione, setAzione] = useState('');
  const [entita, setEntita] = useState('');
  const [userId, setUserId] = useState('');
  const [dataInizio, setDataInizio] = useState(null);
  const [dataFine, setDataFine] = useState(null);

  const debounceRef = useRef(null);

  // --- Debounce sul testo di ricerca (~400ms) -------------------------------
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setQ(qInput.trim());
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [qInput]);

  // --- Carica la lista utenti (solo admin: 403 -> niente filtro autore) ------
  useEffect(() => {
    let attivo = true;
    const caricaUtenti = async () => {
      try {
        const res = await usersService.getAll();
        if (!attivo) return;
        setUtenti(Array.isArray(res.data) ? res.data : []);
        setMostraFiltroAutore(true);
      } catch (err) {
        // 403 (non admin) o altro errore: il filtro autore semplicemente non c'è
        if (attivo) setMostraFiltroAutore(false);
      }
    };
    caricaUtenti();
    return () => {
      attivo = false;
    };
  }, []);

  // --- Carica le voci dell'audit log quando cambiano i filtri ----------------
  const caricaVoci = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (azione) params.azione = azione;
      if (entita) params.entita = entita;
      if (userId) params.user_id = userId;
      if (dataInizio && dataInizio.isValid()) params.data_inizio = dataInizio.format('YYYY-MM-DD');
      if (dataFine && dataFine.isValid()) params.data_fine = dataFine.format('YYYY-MM-DD');

      const res = await auditService.getAll(params);
      setVoci(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Errore nel caricamento del registro attività:', err);
      setError('Errore nel caricamento del registro attività. Riprova.');
      setVoci([]);
    } finally {
      setLoading(false);
    }
  }, [q, azione, entita, userId, dataInizio, dataFine]);

  useEffect(() => {
    caricaVoci();
  }, [caricaVoci]);

  // --- Azzera tutti i filtri -------------------------------------------------
  const azzeraFiltri = () => {
    setQInput('');
    setQ('');
    setAzione('');
    setEntita('');
    setUserId('');
    setDataInizio(null);
    setDataFine(null);
  };

  const filtriAttivi =
    !!q || !!azione || !!entita || !!userId || !!dataInizio || !!dataFine;

  const datePickerSlotProps = {
    textField: {
      fullWidth: true,
      size: 'small',
      sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } },
    },
    field: { clearable: true },
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f7', minHeight: '100vh' }}>
      {/* ---------------------- Header ---------------------- */}
      <Box sx={{ bgcolor: '#1a1a1a', pt: { xs: 3, sm: 4 }, pb: { xs: 6, sm: 8 }, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="lg" disableGutters>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: 'rgba(253, 0, 0, 0.15)',
                color: BRAND,
                width: { xs: 48, sm: 56 },
                height: { xs: 48, sm: 56 },
              }}
            >
              <FactCheckIcon sx={{ fontSize: { xs: 26, sm: 30 } }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.5rem', sm: '2rem' } }}
              >
                Registro Attività
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Chi ha creato, modificato e assegnato cosa
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ---------------------- Contenuto ---------------------- */}
      <Container
        maxWidth="lg"
        sx={{
          mt: { xs: -4, sm: -5 },
          pb: { xs: 12, sm: 6 },
          position: 'relative',
          zIndex: 2,
        }}
      >
        {/* ---------- Barra filtri ---------- */}
        <Paper
          elevation={2}
          sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, mb: 3 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <FilterAltIcon sx={{ color: BRAND }} fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Filtri
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={2}
            sx={{ flexWrap: 'wrap', gap: 2, '& > *': { minWidth: { xs: '100%', sm: 200 }, flex: { xs: '1 1 100%', sm: '1 1 200px' } } }}
          >
            {/* Ricerca testo */}
            <TextField
              label="Cerca"
              placeholder="Cerca nel registro…"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: qInput ? (
                  <InputAdornment position="end">
                    <ClearIcon
                      fontSize="small"
                      sx={{ cursor: 'pointer', color: 'text.secondary' }}
                      onClick={() => setQInput('')}
                    />
                  </InputAdornment>
                ) : null,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {/* Azione */}
            <TextField
              select
              label="Azione"
              value={azione}
              onChange={(e) => setAzione(e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">Tutte</MenuItem>
              {AZIONE_OPTIONS.map((a) => (
                <MenuItem key={a} value={a}>
                  {AZIONI[a].label}
                </MenuItem>
              ))}
            </TextField>

            {/* Entità */}
            <TextField
              select
              label="Entità"
              value={entita}
              onChange={(e) => setEntita(e.target.value)}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">Tutte</MenuItem>
              {ENTITA_OPTIONS.map((en) => (
                <MenuItem key={en} value={en}>
                  {ENTITA[en].label}
                </MenuItem>
              ))}
            </TextField>

            {/* Autore (solo admin) */}
            {mostraFiltroAutore && (
              <TextField
                select
                label="Autore"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {utenti.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.nome || u.username || `Utente ${u.id}`}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/* Data inizio */}
            <DatePicker
              label="Da"
              value={dataInizio}
              onChange={(newValue) => setDataInizio(newValue)}
              maxDate={dataFine || undefined}
              format="DD/MM/YYYY"
              slotProps={datePickerSlotProps}
            />

            {/* Data fine */}
            <DatePicker
              label="A"
              value={dataFine}
              onChange={(newValue) => setDataFine(newValue)}
              minDate={dataInizio || undefined}
              format="DD/MM/YYYY"
              slotProps={datePickerSlotProps}
            />
          </Stack>

          {/* Azzera filtri */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              onClick={azzeraFiltri}
              disabled={!filtriAttivi}
              startIcon={<ClearIcon />}
              size="small"
              sx={{
                color: BRAND,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { bgcolor: 'rgba(253, 0, 0, 0.06)' },
              }}
            >
              Azzera filtri
            </Button>
          </Box>
        </Paper>

        {/* ---------- Errore ---------- */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* ---------- Risultati ---------- */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
            <CircularProgress sx={{ color: BRAND }} />
            <Typography variant="body2" color="text.secondary">
              Caricamento attività…
            </Typography>
          </Box>
        ) : voci.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, sm: 6 },
              borderRadius: 3,
              textAlign: 'center',
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: '#fff',
            }}
          >
            <HistoryIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Nessuna attività registrata
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filtriAttivi
                ? 'Nessun risultato per i filtri selezionati. Prova ad azzerarli.'
                : 'Le attività di creazione, modifica e assegnazione compariranno qui.'}
            </Typography>
          </Paper>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, ml: 0.5 }}>
              {voci.length} {voci.length === 1 ? 'attività' : 'attività'}
            </Typography>
            <Stack spacing={isMobile ? 1.5 : 2}>
              {voci.map((voce) => (
                <VoceAudit key={voce.id} voce={voce} />
              ))}
            </Stack>
          </>
        )}
      </Container>
    </Box>
  );
};

export default RegistroAttivita;
